import { Colors } from '@oy/uicore';
import {
  Button,
  Checkbox,
  Loader,
  Modal,
  Padder,
  Steppers,
  Text,
} from '@oy/uidesktop';
import { useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useScrolledToBottom, useTranslate } from 'src/hooks';
import AlreadySignedModal from './AlreadySignedModal';
import CloseModalConfirmation from './CloseModalConfirmation';
import styles from './SingLegalDocumentModal.module.css';
import SuccessModal from './SuccessModal';
import { useSignLegalDocumentModalImpl } from './useSignLegalDocumentModalImpl';

pdfjs.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.12.313/pdf.worker.js';

export const SignLegalDocumentModal = () => {
  const { isBottom, setRef, resetPosition } = useScrolledToBottom();
  const { getTranslation } = useTranslate();
  const windowWidth = useRef(window.innerWidth);

  const [isTooltipVisible, setTooltipVisible] = useState(false);

  const {
    state,
    onCloseModal,
    onNextDocument,
    onLoadSuccess,
    onChecked,
    getStepTitle,
    onCloseModalConfirmation,
    onCancelCloseModalConfirmation,
    onCloseModalSuccess,
    onSubmit,
    onCloseAlreadySigned,
  } = useSignLegalDocumentModalImpl(resetPosition);
  const {
    activeIndex,
    show,
    numPages,
    currentFile,
    isCurrentDocsSigned,
    legalDocs,
    showCloseModal,
    showSuccessModal,
    isLoading,
    isSubmitting,
    isAlreadySigned,
  } = state;

  const handleMouseEnter = () => {
    setTooltipVisible(true);
  };

  const handleMouseLeave = () => {
    setTooltipVisible(false);
  };

  return (
    <>
      <AlreadySignedModal
        showModal={isAlreadySigned}
        onCloseModal={onCloseAlreadySigned}
      />
      <SuccessModal
        showSuccessModal={showSuccessModal}
        onCloseModalConfirmation={onCloseModalSuccess}
      />
      <CloseModalConfirmation
        showCloseModal={showCloseModal}
        onCancelCloseModalConfirmation={onCancelCloseModalConfirmation}
        onCloseModalConfirmation={onCloseModalConfirmation}
      />
      {isSubmitting && <Loader isUsingBackdrop />}
      <Modal show={show} onClose={onCloseModal}>
        <Modal.Header showCloseButton showDivider>
          {getTranslation('common_PKS')}
        </Modal.Header>
        <Modal.Body style={{ maxWidth: '100%', textAlign: 'center' }}>
          <div>
            <Text textStyle={Text.Style.BodyMdRegular}>
              {getTranslation('tickbox_pks_description')}
            </Text>
            <Padder
              vertical={Padder.Size.M}
              className={styles.stepperContainer}
            >
              <Steppers
                direction={Steppers.Direction.HORIZONTAL}
                activeIndex={activeIndex}
                type={Steppers.Type.LINEAR}
                steps={legalDocs.map(item => {
                  return { label: getStepTitle(item) };
                })}
                maxItemsPerRow={3}
                completedDotColor={Colors.GREEN100}
              />
            </Padder>
            <div ref={setRef} className={styles.pdfReaderContainer}>
              {isLoading ? (
                <Loader />
              ) : (
                <Document
                  file={currentFile}
                  onLoadSuccess={({ numPages }) => onLoadSuccess(numPages)}
                  className={styles.check}
                >
                  {Array.from(new Array(numPages), (_, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      width={0.8 * windowWidth.current}
                    />
                  ))}
                </Document>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.footerContainer}>
          {legalDocs.length > 0 && (
            <div className={styles.tooltipContainer}>
              <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <Checkbox
                  type="box"
                  title={`${getTranslation(
                    'tickbox_pks_common_agree',
                  )} ${getStepTitle(legalDocs[activeIndex])}`}
                  checked={isCurrentDocsSigned}
                  onChange={onChecked}
                  disabled={!isBottom}
                />
              </div>
              {!isBottom && isTooltipVisible && (
                <div className={styles.customTooltip}>
                  <Text
                    textStyle={Text.Style.BodySmRegular}
                    color={Colors.WHITE}
                  >
                    {getTranslation('kyb_vidaAgreement_scrollInfo')}
                  </Text>
                </div>
              )}
            </div>
          )}

          <Button
            className={styles.buttonContainer}
            onClick={
              activeIndex < legalDocs.length - 1 ? onNextDocument : onSubmit
            }
            disabled={!isCurrentDocsSigned || !isBottom}
          >
            {getTranslation('common_approve')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
